import moduleInfo from '../module.json';
import type { AppRuntime } from '@ayocore/platform/client/runtime/AppRuntime';
import {
  IconShield,
  IconUsers,
  IconUserCheck,
  IconUser,
  IconUsersGroup,
} from '@tabler/icons-react';
import { lazy } from 'react';
import { AuthController } from './AuthController';
import { ProfileDockPanel } from './components/ProfileDockPanel';
import { ProfileDockIcon } from './components/ProfileDockIcon';

const isAdmin = () => {
  const user = AuthController.getInstance().getSnapshot().user;
  return user?.roles?.includes('ADMIN') ?? false;
};

/**
 * Бутстрап модуля Identity (клиент)
 * Регистрирует навигацию через рантайм с динамическим содержимым на основе роли пользователя.
 */
export async function setup(app: AppRuntime) {
  const identity = app.useModule(moduleInfo.name, {
    version: moduleInfo.version,
    description: moduleInfo.description,
  });

  // Группа «Администрирование» — объединяет Пользователи, Роли, Группы
  const groupAdmin = identity.createNavigationGroup({
    key: 'admin',
    label: 'Администрирование',
    icon: IconShield,
    order: 1,
  });

  identity.useDock({
    id: 'profile-dock',
    label: 'Профиль',
    icon: ProfileDockIcon,
    component: ProfileDockPanel,
    order: 100,
  });

  identity.useNavigation([
    // Профиль — скрыт из меню, доступ по клику на аватар
    {
      icon: IconShield,
      label: 'Профиль',
      url: '/identity',
      component: lazy(() => import('./components/AuthenticationPage')),
      hiddenFromMenu: true,
    },
    // Админские пункты в группе «Администрирование»
    {
      group: groupAdmin,
      icon: IconUsers,
      label: 'Пользователи',
      url: '/identity/users',
      component: lazy(() => import('./components/UserList')),
      guard: isAdmin,
    },
    {
      group: groupAdmin,
      icon: IconUserCheck,
      label: 'Роли',
      url: '/identity/roles',
      component: lazy(() => import('./components/RoleList')),
      guard: isAdmin,
    },
    {
      group: groupAdmin,
      icon: IconUsersGroup,
      label: 'Группы',
      url: '/identity/groups',
      component: lazy(() => import('./components/GroupList')),
      guard: isAdmin,
    },
    // Скрытые роуты (доступ по прямой ссылке)
    {
      icon: IconUsersGroup,
      label: 'Группа',
      url: '/identity/groups/:id',
      component: lazy(() => import('./components/GroupView')),
      guard: isAdmin,
      hiddenFromMenu: true,
    },
    {
      icon: IconUserCheck,
      label: 'Роль - Методы',
      url: '/identity/roles/:id/actions',
      component: lazy(() => import('./components/RoleView')),
      guard: isAdmin,
      hiddenFromMenu: true,
    },
    {
      icon: IconUserCheck,
      label: 'Роль - Репозитории',
      url: '/identity/roles/:id/repositories',
      component: lazy(() => import('./components/RoleRepositoriesView')),
      guard: isAdmin,
      hiddenFromMenu: true,
    },
    {
      icon: IconUser,
      label: 'Профиль пользователя',
      url: '/identity/profile/:username',
      component: lazy(() => import('./components/UserProfilePage')),
      hiddenFromMenu: true,
    },
  ]);
}
